import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { createContactUsForm } from '../../../Services/APIs/ContactUsPage';

import Spinner from '../Loading/Spinner';

import './Modal.scss';

import Button from '../../CommonComponents/Button';

import { useAuth } from '../../../hoc/AuthContext';
import useIpCountryDefault from '../../../hooks/useIpCountryDefault';
import { getDefaultIpCountry, normalizeCountryCallingCode } from '../../../Utils/IpCountry';

export default function ContactUsModal({ isOpen = false, integratorTheme = {}, onClose = () => {} }) {
  const { userData } = useAuth();
  const ipCountryDefault = useIpCountryDefault();
  const countryCodeTouchedRef = useRef(false);
  
  const [formData, setFormData] = useState({ name: userData?.name || '', email: userData?.email || '', message: '' });
  const [errors, setErrors] = useState({});
  const [submittedMessage, setSubmittedMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(userData?.mobile_number || '');
  const [phoneError, setPhoneError] = useState('');
  const [countryCode, setCountryCode] = useState(() => userData?.country_code || getDefaultIpCountry().dialCode);

  useEffect(() => {
    if (!userData?.country_code && !countryCodeTouchedRef.current && !phone) {
      setCountryCode(ipCountryDefault.dialCode);
    }
  }, [ipCountryDefault.dialCode, phone, userData?.country_code]);

  // Handle phone number changes
  const handlePhoneChange = (value, country) => {
    // Remove country code from the value and set only the phone number
    const dialCode = country.dialCode || '';
    let phoneNumber = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
    // Remove any leading zeros or spaces
    phoneNumber = phoneNumber.replace(/^0+/, '').trim();
    setPhone(phoneNumber);
    countryCodeTouchedRef.current = true;
    setCountryCode(dialCode);
    setPhoneError(''); // Clear phone error when user types
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setErrors({
      ...errors,
      [name]: ''
    });
  };

  // Validate the form
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Full Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = 'Full Name is invalid';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validate();

    if (!phone) {
      setPhoneError('Phone number is required');
      return;
    }
    setPhoneError('');

    if (Object?.keys(formErrors)?.length === 0) {
      try {
        setLoading(true);
        const data = {
          ...formData,
          country_code: normalizeCountryCallingCode(countryCode, { withPlus: true }),
          mobile_number: phone,
          type: 'get_in_touch'
        };
        const result = await createContactUsForm(data);

        if (result?.status === 200) {
          setSubmittedMessage(true);
          // Reset form data
          setFormData({ name: '', email: '', message: '' });
          setPhone('');
          countryCodeTouchedRef.current = false;
          setCountryCode(userData?.country_code || ipCountryDefault.dialCode);
          // Clear the success message after 2 seconds and close modal
          setTimeout(() => {
            setSubmittedMessage(false);
            onClose();
          }, 2000);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(formErrors);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contactModalOverlay" onClick={onClose}>
      <div className="contactModal" onClick={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <h2>Contact Us</h2>
          <button className="closeBtn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form className="modalForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <p className="error">{errors.name}</p>}
          </div>

          <div className="formGroup">
            <input
              type="email"
              name="email"
              placeholder="Work Email"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <p className="error">{errors.email}</p>}
          </div>

          <div className="formGroup">
            <PhoneInput
              country={ipCountryDefault.countryIso2}
              value={normalizeCountryCallingCode(countryCode) + phone}
              onChange={handlePhoneChange}
              inputClass="contact-phone-input"
              inputProps={{ name: 'phone', required: true, autoFocus: false }}
            />
            {phoneError && <p className="error phoneError">{phoneError}</p>}
          </div>

          <div className="formGroup">
            <textarea
              name="message"
              placeholder="How can we help you?"
              value={formData.message}
              onChange={handleChange}
              rows={5}
            />
            {errors.message && <p className="error">{errors.message}</p>}
          </div>

          <div className="modalActions">
            <Button size='md' variant='secondary' type="button" className="cancelBtn" onClick={onClose} disabled={loading} 
              theme={integratorTheme}>
              Cancel
            </Button>
            <Button size='md' variant='primary' type="submit" className="submitBtn" theme={integratorTheme} disabled={loading}>
              {loading ? <Spinner /> : "Submit form"}
            </Button>
          </div>

          {submittedMessage && (
            <p className="success-message">Thank you for reaching out! We'll be in touch soon.</p>
          )}
        </form>
      </div>
    </div>
  );
};

ContactUsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
